import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { useActiveStore } from "../lib/activeStore.js";
import {
  subscribeUnits, subscribeStore, subscribeRecentSnapshots,
  chargeCycleStatus, daysSinceCheck, relativeTime, CHARGE_CYCLE,
} from "../lib/data.js";

// Weekly report — print-optimized SEVRIC weekly recap. Designed to be
// opened in a new tab from the Manager view, then "Save as PDF" /
// "Print" via the browser. Clean white background, black text, single
// brand mark and (optional) store logo, page-break-friendly.
//
// Layout (letter / A4 portrait):
//   ┌─────────────────────────────────────────────────────────┐
//   │  SEVRIC (B&W wordmark)         [Store logo if uploaded] │
//   │  Battery Care · Weekly Report · Store · Date            │
//   ├─────────────────────────────────────────────────────────┤
//   │  [Active]  [Overdue]  [Due Soon]  [Compliance %]        │
//   │  ┌─── 4-week compliance trend ──────────────────────┐   │
//   │  │  sparkline                                        │   │
//   │  └───────────────────────────────────────────────────┘   │
//   │  Units needing attention (table)                         │
//   │  Full inventory (table)                                  │
//   │  Generated by SEVRIC · timestamp · hex aperture          │
//   └─────────────────────────────────────────────────────────┘
//
// On-screen the page shows a small "Print / Save as PDF" CTA at the top
// that's hidden in print via @media print. The page chrome (.report-shell)
// uses pure HTML+CSS — no SEVRIC dark theme — so it prints cleanly.
export function Report() {
  const { profile } = useAuth();
  const activeStore = useActiveStore();
  const [units, setUnits] = useState([]);
  const [store, setStore] = useState(null);
  const [snapshots, setSnapshots] = useState([]);

  useEffect(() => {
    if (!profile?.tenantId || !activeStore) return;
    return subscribeUnits(
      { tenantId: profile.tenantId, storeId: activeStore },
      (u) => setUnits(u || []),
    );
  }, [profile?.tenantId, activeStore]);

  useEffect(() => {
    if (!activeStore) return;
    return subscribeStore(activeStore, (s) => setStore(s));
  }, [activeStore]);

  useEffect(() => {
    if (!profile?.tenantId || !activeStore) return;
    return subscribeRecentSnapshots(
      { tenantId: profile.tenantId, storeId: activeStore, days: 28 },
      (rows) => setSnapshots(rows || []),
    );
  }, [profile?.tenantId, activeStore]);

  const stats = useMemo(() => {
    let overdue = 0, due = 0, charging = 0;
    const overdueList = [], dueList = [];
    for (const u of units) {
      if (u.sold) continue;
      const cc = chargeCycleStatus(u);
      if (cc === "overdue")  { overdue++;  overdueList.push(u); }
      else if (cc === "due") { due++;      dueList.push(u); }
      else if (cc === "charging") charging++;
    }
    const total = units.filter((u) => !u.sold).length;
    const inCycle = total - overdue - due;
    const compliancePct = total > 0 ? Math.round((inCycle / total) * 100) : 100;
    return { overdue, due, charging, total, compliancePct, overdueList, dueList };
  }, [units]);

  const ranked = useMemo(
    () => units.filter((u) => !u.sold).slice().sort((a, b) => {
      const ad = daysSinceCheck(a) ?? (a.daysOnLot ?? 0);
      const bd = daysSinceCheck(b) ?? (b.daysOnLot ?? 0);
      return bd - ad;
    }),
    [units],
  );

  const today = new Date();
  const dateLong = today.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const dateShort = today.toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" });
  const storeName = store?.name || activeStore || "—";
  const storeLogo = store?.logoUrl || store?.logoMonoUrl || null;

  // Compliance delta vs 4 weeks ago — pulled directly from the first and
  // last entries of the snapshot range (which is already sorted ascending).
  const trendNow = snapshots[snapshots.length - 1]?.compliancePct ?? stats.compliancePct;
  const trendFirst = snapshots[0]?.compliancePct ?? stats.compliancePct;
  const trendDelta = trendNow - trendFirst;

  return (
    <main className="report-shell">
      <style>{`
        /* Print-optimized chrome. The rest of the SEVRIC battery app is
           dark-themed, but reports are designed to be printed on white
           paper or saved as PDF — so this page resets to a clean white
           document with black text. */
        .report-shell {
          background: #ffffff;
          color: #1a1a1a;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
          font-size: 12px;
          line-height: 1.55;
          min-height: 100vh;
          padding: 40px 48px 32px;
          max-width: 8.5in;
          margin: 0 auto;
        }
        .report-actions {
          display: flex; gap: 10px; justify-content: flex-end;
          margin-bottom: 18px;
        }
        .report-actions button, .report-actions a {
          font: 500 12px 'Inter', sans-serif;
          padding: 6px 14px; border-radius: 6px;
          border: 1px solid #d1d5db; background: #fff; color: #1a1a1a;
          cursor: pointer; text-decoration: none;
        }
        .report-actions button.primary {
          background: #1a1a1a; color: #fff; border-color: #1a1a1a;
        }
        .report-hdr {
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center; gap: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #1a1a1a;
        }
        .report-hdr-sevric {
          height: 56px; width: auto;
          display: block;
        }
        .report-hdr-store-logo {
          max-height: 64px; max-width: 200px;
          object-fit: contain;
        }
        .report-hdr-title {
          text-align: center;
        }
        .report-hdr-title .eyebrow {
          font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
          color: #6b7280; margin-bottom: 4px;
        }
        .report-hdr-title h1 {
          font-family: 'Crimson Pro', 'Times New Roman', serif;
          font-optical-sizing: auto; font-weight: 500;
          font-size: 22px; line-height: 1.1; letter-spacing: -0.025em;
          margin: 0;
        }
        .report-hdr-title .meta {
          font-size: 11px; color: #6b7280; margin-top: 4px;
        }
        .report-kpis {
          display: grid; grid-template-columns: repeat(4, 1fr);
          gap: 0; border: 1px solid #e5e7eb; border-radius: 8px;
          margin: 22px 0 14px; overflow: hidden;
        }
        .report-kpi {
          padding: 12px 14px;
          border-right: 1px solid #e5e7eb;
        }
        .report-kpi:last-child { border-right: 0; }
        .report-kpi .label {
          font-size: 10px; letter-spacing: 0.10em; text-transform: uppercase;
          color: #6b7280; margin-bottom: 4px;
        }
        .report-kpi .value {
          font-family: 'Crimson Pro', serif;
          font-weight: 500; font-size: 26px; line-height: 1;
          letter-spacing: -0.04em;
        }
        .report-kpi .sub {
          font-size: 11px; color: #6b7280; margin-top: 4px;
        }
        .report-kpi.danger .value { color: #7C2935; }
        .report-kpi.warn   .value { color: #B05E13; }
        .report-kpi.ok     .value { color: #2A6D38; }
        .report-trend {
          margin: 18px 0; padding: 14px 18px;
          border: 1px solid #e5e7eb; border-radius: 8px;
        }
        .report-trend .label {
          font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase;
          color: #6b7280; margin-bottom: 10px;
          display: flex; justify-content: space-between; align-items: baseline;
        }
        .report-trend .delta {
          font-size: 11px; color: #1a1a1a; text-transform: none; letter-spacing: 0;
        }
        .report-trend .delta.up   { color: #2A6D38; }
        .report-trend .delta.down { color: #7C2935; }
        .report-section {
          margin: 26px 0 12px;
          page-break-inside: avoid;
        }
        .report-section h2 {
          font-family: 'Crimson Pro', serif;
          font-weight: 500; font-size: 16px;
          letter-spacing: -0.02em;
          margin: 0 0 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid #1a1a1a;
        }
        table.report-table {
          width: 100%; border-collapse: collapse; font-size: 11px;
        }
        table.report-table th {
          text-align: left; font-weight: 500;
          padding: 7px 10px; background: #f3f4f6;
          font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase;
          color: #374151; border-bottom: 1px solid #d1d5db;
        }
        table.report-table td {
          padding: 7px 10px; border-bottom: 1px solid #f1f1f1;
        }
        table.report-table tr:nth-child(even) td {
          background: #fafafa;
        }
        table.report-table td.right { text-align: right; }
        table.report-table td.mono  { font-family: ui-monospace, 'SF Mono', monospace; }
        table.report-table td.status-overdue { color: #7C2935; font-weight: 600; }
        table.report-table td.status-due     { color: #B05E13; font-weight: 600; }
        table.report-table td.status-new     { color: #374151; }
        table.report-table td.status-ok      { color: #2A6D38; }
        table.report-table td.status-charging{ color: #2E5694; font-weight: 600; }
        .report-foot {
          margin-top: 32px; padding-top: 14px;
          border-top: 1px solid #e5e7eb;
          display: flex; justify-content: space-between; align-items: center;
          font-size: 10px; color: #9ca3af; letter-spacing: 0.04em;
        }
        .report-foot .mark {
          width: 18px; height: 18px;
        }
        @media print {
          @page { size: letter; margin: 0.5in; }
          .report-shell { padding: 0; max-width: none; }
          .report-actions { display: none; }
          .report-section { page-break-inside: avoid; }
          table.report-table tr { page-break-inside: avoid; }
        }
      `}</style>

      <div className="report-actions">
        <Link to="/manager">← Back</Link>
        <button type="button" className="primary" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
      </div>

      <header className="report-hdr">
        <div>
          {/* SEVRIC B&W wordmark — inlined SVG so it prints cleanly even if
              external font/image assets fail (offline browser print). Same
              path data as the marketing lockup, recolored to pure black.
              ViewBox sized for the larger 56px header treatment so the
              wordmark reads at masthead weight, not subtitle weight. */}
          <svg className="report-hdr-sevric" viewBox="0 0 320 80" xmlns="http://www.w3.org/2000/svg" aria-label="SEVRIC">
            <g transform="translate(2 8)">
              <polygon points="62,32 50,52.8 24,52.8 12,32 24,11.2 50,11.2"
                       fill="none" stroke="#000" strokeWidth="3" strokeLinejoin="miter" />
              <polygon points="46,32 40,42.4 30,42.4 24,32 30,21.6 40,21.6"
                       fill="#000" />
            </g>
            <text x="84" y="52"
                  fontFamily="Inter, system-ui, sans-serif"
                  fontWeight="300"
                  fontSize="34"
                  letterSpacing="9"
                  fill="#000">SEVRIC</text>
          </svg>
        </div>
        <div className="report-hdr-title">
          <div className="eyebrow">Battery Care · Weekly Report</div>
          <h1>{storeName}</h1>
          <div className="meta">{dateLong}</div>
        </div>
        <div style={{ minWidth: 100, textAlign: "right" }}>
          {storeLogo ? (
            <img className="report-hdr-store-logo" src={storeLogo} alt={`${storeName} logo`} />
          ) : null}
        </div>
      </header>

      <div className="report-kpis">
        <div className={"report-kpi " + (stats.compliancePct >= 90 ? "ok" : stats.compliancePct >= 70 ? "warn" : "danger")}>
          <div className="label">Compliance</div>
          <div className="value">{stats.compliancePct}%</div>
          <div className="sub">{Math.max(0, stats.total - stats.overdue - stats.due)} of {stats.total} on schedule</div>
        </div>
        <div className="report-kpi danger">
          <div className="label">Overdue</div>
          <div className="value">{stats.overdue}</div>
          <div className="sub">&gt; {CHARGE_CYCLE.overdueDays} days since charge</div>
        </div>
        <div className="report-kpi warn">
          <div className="label">Due soon</div>
          <div className="value">{stats.due}</div>
          <div className="sub">{CHARGE_CYCLE.dueDays}–{CHARGE_CYCLE.overdueDays} days</div>
        </div>
        <div className="report-kpi">
          <div className="label">Active vehicles</div>
          <div className="value">{stats.total}</div>
          <div className="sub">in the lot</div>
        </div>
      </div>

      {snapshots.length >= 2 && (
        <section className="report-trend">
          <div className="label">
            <span>28-day compliance trend</span>
            <span className={"delta " + (trendDelta > 0 ? "up" : trendDelta < 0 ? "down" : "")}>
              {trendDelta > 0 ? "↑" : trendDelta < 0 ? "↓" : "→"} {trendDelta >= 0 ? "+" : ""}{trendDelta}pp vs 4 wks ago
            </span>
          </div>
          <ReportSparkline values={snapshots.map((s) => s.compliancePct ?? 0)} />
        </section>
      )}

      {(stats.overdueList.length > 0 || stats.dueList.length > 0) && (
        <section className="report-section">
          <h2>Units needing attention this week ({stats.overdueList.length + stats.dueList.length})</h2>
          <table className="report-table">
            <thead>
              <tr>
                <th>Stock #</th>
                <th>Year / Model</th>
                <th>Status</th>
                <th className="right">Days on lot</th>
                <th className="right">Since charge</th>
                <th>Lot</th>
              </tr>
            </thead>
            <tbody>
              {[...stats.overdueList, ...stats.dueList].map((u) => {
                const cc = chargeCycleStatus(u);
                const ds = daysSinceCheck(u);
                return (
                  <tr key={u.id}>
                    <td className="mono">{u.stockNumber || "—"}</td>
                    <td>{[u.year, u.make, u.model, u.trim].filter(Boolean).join(" ") || "—"}</td>
                    <td className={`status-${cc}`}>{cc === "overdue" ? "Overdue" : "Due Soon"}</td>
                    <td className="mono right">{u.daysOnLot ?? 0}</td>
                    <td className="mono right">{ds != null ? `${ds}d` : "Never"}</td>
                    <td>{u.lotPos || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      <section className="report-section">
        <h2>Full inventory ({ranked.length})</h2>
        <table className="report-table">
          <thead>
            <tr>
              <th>Stock #</th>
              <th>Year / Model</th>
              <th>Status</th>
              <th className="right">Days on lot</th>
              <th className="right">Since charge</th>
              <th>Last action</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((u) => {
              const cc = chargeCycleStatus(u);
              const ds = daysSinceCheck(u);
              const label = cc === "overdue" ? "Overdue"
                          : cc === "due"     ? "Due Soon"
                          : cc === "charging"? "Charging"
                          : cc === "new"     ? "New"
                          : "OK";
              return (
                <tr key={u.id}>
                  <td className="mono">{u.stockNumber || "—"}</td>
                  <td>{[u.year, u.make, u.model, u.trim].filter(Boolean).join(" ") || "—"}</td>
                  <td className={`status-${cc}`}>{label}</td>
                  <td className="mono right">{u.daysOnLot ?? 0}</td>
                  <td className="mono right">{ds != null ? `${ds}d` : "Never"}</td>
                  <td>{u.lastAction || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <footer className="report-foot">
        <span>
          Generated by SEVRIC · {today.toLocaleDateString()} {today.toLocaleTimeString()}
          {" · "}sevric.com
        </span>
        <svg className="mark" viewBox="0 0 240 240" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <polygon points="200,120 160,189.28 80,189.28 40,120 80,50.72 160,50.72"
                   fill="none" stroke="#000" strokeWidth="10" strokeLinejoin="miter" />
          <polygon points="156,120 138,151.18 102,151.18 84,120 102,88.82 138,88.82"
                   fill="#000" />
        </svg>
      </footer>
    </main>
  );
}

// Print-friendly sparkline. Pure black stroke, no fill, no animation.
function ReportSparkline({ values = [], width = 760, height = 70 }) {
  const clean = values.filter((v) => typeof v === "number" && !Number.isNaN(v));
  if (clean.length < 2) {
    return <div style={{ fontSize: 11, color: "#9ca3af" }}>— Trend building (need at least 2 daily snapshots)</div>;
  }
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const range = max - min || 1;
  const padY = 6;
  const usableH = height - padY * 2;
  const stepX = clean.length > 1 ? width / (clean.length - 1) : width;
  const points = clean.map((v, i) => [i * stepX, padY + (1 - (v - min) / range) * usableH]);
  const polyline = points.map((p) => p.join(",")).join(" ");
  const last = points[points.length - 1];
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={polyline} fill="none" stroke="#1a1a1a" strokeWidth="1.4"
                strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill="#1a1a1a" />
    </svg>
  );
}
